import {
  Alert,
  AutoComplete,
  Button,
  Checkbox,
  Flex,
  Form,
  Input,
  Select,
  Spin,
  Typography,
} from 'antd';
import { Link } from 'react-router-dom';

import { Phone } from '@app/components';
import {
  EMAIL_LENGTH_VALIDATION,
  EMAIL_TYPE_VALIDATION,
  PNG,
} from '@app/constants';

import { useRegistration } from '@pages/Registration/hooks/useRegistration';
import { useRegistrationEffects } from '@pages/Registration/hooks/useRegistrationEffects';

import type { FC } from 'react';

import '@pages/Registration/styles/Registration.scss';

const { Text, Title } = Typography;
const { Item } = Form;

const DEFAULT_TIMEZONE = 'Europe/Moscow';

export const Registration: FC = () => {
  const {
    isButtonLoading,
    isCorrectCaptchaAnswer,
    captchaImage,
    setCaptchaImage,
    setUserCaptchaValue,
    selectedCityId,
    setSelectedCityId,
    setCaptchaId,
    onFormFinish,
    setPhoneValidation,
    form,
  } = useRegistration();

  const {
    countries,
    handleCityChange,
    handleCitySelect,
    options,
    city,
    timezonesData,
  } = useRegistrationEffects({
    setCaptchaImage,
    setSelectedCityId,
    setCaptchaId,
  });

  if (!countries) {
    return <Spin spinning />;
  }

  return (
    <div className="Registration">
      <div className="Registration__form-container">
        <Title
          className="Registration__title"
          level={3}
          data-testid="register-title"
        >
          Регистрация
        </Title>
        <Form
          form={form}
          name="registration"
          initialValues={{
            timezone: DEFAULT_TIMEZONE,
            Phone: {
              Phone: '7',
              CountryCode: 2,
            },
          }}
          onFinish={onFormFinish}
        >
          <Item
            name="firstName"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="name-alert">Пожалуйста, введите имя!</span>
                ),
              },
              {
                min: 1,
                max: 80,
                message: 'Имя должно быть от 1 до 80 символов!',
              },
            ]}
          >
            <Input placeholder="Имя" data-testid="input-name" maxLength={80} />
          </Item>
          <Item
            name="lastName"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="surname-alert">
                    Пожалуйста, введите фамилию!
                  </span>
                ),
              },
              {
                min: 1,
                max: 80,
                message: 'Фамилия должна быть от 1 до 80 символов!',
              },
            ]}
          >
            <Input
              placeholder="Фамилия"
              data-testid="input-surname"
              maxLength={80}
            />
          </Item>

          <Item name="Phone">
            <Phone
              name="Phone"
              required
              onValidate={setPhoneValidation}
              size="small"
              countries={countries}
            />
          </Item>

          <Item
            name="email"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="email-alert">
                    Пожалуйста, введите email!
                  </span>
                ),
              },
              EMAIL_TYPE_VALIDATION,
              EMAIL_LENGTH_VALIDATION,
            ]}
          >
            <Input
              placeholder="Email"
              data-testid="input-email"
              maxLength={100}
            />
          </Item>
          <Item
            name="organization"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="organization-alert">
                    Пожалуйста, введите название организации!
                  </span>
                ),
              },
              {
                min: 2,
                max: 200,
                message: (
                  <span data-testid="organization-alert">
                    Название организации должно быть от 2 до 200 символов!
                  </span>
                ),
              },
            ]}
          >
            <Input
              placeholder="Название организации"
              data-testid="input-organization"
              maxLength={200}
            />
          </Item>
          <Item
            name="city"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="city-alert">
                    Пожалуйста, выберите город!
                  </span>
                ),
              },
              () => ({
                validator() {
                  if (!selectedCityId) {
                    return Promise.reject(
                      <span data-testid="city-alert">
                        Пожалуйста, выберите город из выпадающего списка!
                      </span>
                    );
                  }
                  return Promise.resolve();
                },
              }),
            ]}
            validateTrigger={['onChange', 'onSelect']}
          >
            <AutoComplete
              data-testid="input-city"
              value={city}
              className="Registration__city-select"
              options={options}
              onSearch={handleCityChange}
              onSelect={handleCitySelect}
              placeholder="Город"
              onKeyDown={(e) => {
                if (e.key === ' ' && !city.length) {
                  e.preventDefault();
                }
              }}
            />
          </Item>
          <Item
            name="timezone"
            rules={[
              {
                required: true,
                message: (
                  <span data-testid="timezone-alert">
                    Пожалуйста, выберите временную зону!
                  </span>
                ),
              },
            ]}
          >
            <Select
              placeholder="Временная зона"
              data-testid="timezone-name"
              options={(timezonesData || []).map(({ Id, Name }) => ({
                value: Id,
                label: Name,
              }))}
            />
          </Item>
          <Item
            name="agreement"
            valuePropName="checked"
            rules={[
              {
                validator: (_, value) =>
                  value
                    ? Promise.resolve()
                    : Promise.reject(
                        <span data-testid="checkbox-alert">
                          Вы должны принять условия пользовательского
                          соглашения!
                        </span>
                      ),
              },
            ]}
          >
            <Checkbox
              className="Registration__agreement"
              data-testid="checkbox-icon"
            >
              Я принимаю{' '}
              <a
                href="https://byhelp.ru/user-agreement"
                target="_blank"
                rel="noopener noreferrer"
              >
                условия пользовательского соглашения
              </a>{' '}
              и разрешаю обработку персональных данных
            </Checkbox>
          </Item>
          {captchaImage && (
            <>
              <Typography className="Registration__captcha-title">
                Защита от ботов
              </Typography>
              <div className="Registration__captcha-container">
                <img
                  className="Registration__captcha-image"
                  src={`data:${PNG};base64,${captchaImage}`}
                  alt="captcha-image"
                />
                <Item
                  label={null}
                  name="captcha"
                  className="Registration__captcha-field-item"
                  rules={[
                    {
                      required: true,
                      message: 'Обязательное поле!',
                    },
                  ]}
                >
                  <Input
                    className="Registration__captcha-field"
                    size="large"
                    onChange={({ target }) =>
                      setUserCaptchaValue(Number(target.value))
                    }
                    data-testid="input-captcha"
                  />
                </Item>
              </div>
            </>
          )}
          {!isCorrectCaptchaAnswer && (
            <div className="Registration__alert-container">
              <Alert
                description="Результат уравнения указан неверно!"
                type="error"
                showIcon
              />
            </div>
          )}
          <Item>
            <Button
              type="primary"
              htmlType="submit"
              className="Registration__submit-button"
              loading={isButtonLoading}
              data-testid="submit"
            >
              Продолжить
            </Button>
          </Item>
        </Form>
        <Flex align="center" flex={1} vertical>
          <Text className="Registration__login-text">Уже есть аккаунт?</Text>
          <Link to={'/authorization'} data-testid="login-link">
            <Button type="link">Авторизоваться</Button>
          </Link>
        </Flex>
      </div>
    </div>
  );
};
